/**
 * WhatsApp AI Dashboard
 * Componente principale per gestione bot WhatsApp e apparati
 *
 * @example
 * ```tsx
 * import { WhatsAppProvider, WhatsAppDashboard } from './whatsapp-dashboard';
 *
 * function App() {
 *   const [health, setHealth] = useState(null);
 *
 *   return (
 *     <WhatsAppProvider config={{ apiBaseUrl: '/api' }}>
 *       <WhatsAppDashboard
 *         health={health}
 *         onRefreshHealth={() => fetchHealth()}
 *       />
 *     </WhatsAppProvider>
 *   );
 * }
 * ```
 */

import { useState, useEffect } from 'react';
import { Server, Database, RefreshCw, ChevronDown, ChevronUp, FlaskConical, X, Wifi, WifiOff, AlertCircle } from 'lucide-react';
import { useWhatsAppConnection, useDevices } from '../hooks';
import { useWhatsAppConfig } from '../WhatsAppProvider';
import { ConfigPanel } from './ConfigPanel';
import { waAuthHeaders } from '../waFetch';
import { ConversationsViewer } from './ConversationsViewer';
import { TestSimulatorPanel } from './TestSimulatorPanel';

interface HealthStatus {
  status: string;
  database: string;
  timestamp: string;
}

interface WhatsAppDashboardProps {
  health: HealthStatus | null;
  onRefreshHealth: () => void;
}

export function WhatsAppDashboard({ health, onRefreshHealth }: WhatsAppDashboardProps) {
  const { apiBaseUrl } = useWhatsAppConfig();

  const {
    status,
    connected,
    qrCode,
    loading: waLoading,
    error: waError,
    connect,
    disconnect
  } = useWhatsAppConnection();

  const {
    devices,
    loading: devicesLoading,
    error: devicesError,
    refresh: refreshDevices,
    createDevice,
    updateDevice,
    deleteDevice
  } = useDevices();

  // Stato accordion sezioni (solo una aperta alla volta)
  const [expandedSection, setExpandedSection] = useState<'devices' | 'conversations' | 'config' | 'test' | null>('devices');
  const [togglingDeviceId, setTogglingDeviceId] = useState<number | null>(null);
  const [deviceToDelete, setDeviceToDelete] = useState<{id: number, name: string} | null>(null);
  const [deletingDevice, setDeletingDevice] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);

  // Bot attivo (da config)
  const [botActive, setBotActive] = useState<boolean | null>(null);

  // Carica stato bot attivo
  useEffect(() => {
    const loadBotStatus = async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/config`, { headers: waAuthHeaders() });
        const data = await res.json();
        if (data.success && data.data) {
          setBotActive(data.data.is_active);
        }
      } catch (err) {
        console.error('Errore caricamento stato bot:', err);
      }
    };
    loadBotStatus();
    // Refresh ogni 10 secondi
    const interval = setInterval(loadBotStatus, 10000);
    return () => clearInterval(interval);
  }, [apiBaseUrl]);

  // Calcola stato aggregato: true se TUTTO ok, false se qualcosa non va
  const isAllOk = connected && health?.status === 'ok' && health?.database === 'connected' && botActive === true;
  const hasError = !connected || health?.status !== 'ok' || health?.database !== 'connected' || botActive === false;

  // Carica tutti i device inclusi i disabilitati
  useEffect(() => {
    refreshDevices(false);
  }, []);

  const [showNewDeviceForm, setShowNewDeviceForm] = useState(false);
  const [editingDevice, setEditingDevice] = useState<any>(null);
  const [customStatus, setCustomStatus] = useState('');
  const [newCustomStatus, setNewCustomStatus] = useState('');
  const [showCustomStatusInput, setShowCustomStatusInput] = useState(false);
  const [showNewCustomStatusInput, setShowNewCustomStatusInput] = useState(false);
  const [newDevice, setNewDevice] = useState({
    customer_name: '',
    customer_phone: '',
    device_code: '',
    device_name: '',
    device_type: 'turbina' as 'turbina' | 'iniettori' | 'fap' | 'other',
    location: '',
    current_status: 'in attesa di conferma',
    estimate_amount: null as number | null,
    is_paid: false,
    payment_link: '',
    acceptance_date: null as string | null,
    approval_date: null as string | null,
    estimated_delivery_date: null as string | null
  });

  const handleConnect = async () => {
    await connect();
  };

  const handleDisconnect = async () => {
    await disconnect();
  };

  const handleCreateDevice = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await createDevice(newDevice);
    if (success) {
      setShowNewDeviceForm(false);
      setNewCustomStatus('');
      setShowNewCustomStatusInput(false);
      setNewDevice({
        customer_name: '',
        customer_phone: '',
        device_code: '',
        device_name: '',
        device_type: 'turbina',
        location: '',
        current_status: 'in attesa di conferma',
        estimate_amount: null,
        is_paid: false,
        payment_link: '',
        acceptance_date: null,
        approval_date: null,
        estimated_delivery_date: null
      });
    }
  };

  const handleEditDevice = (device: any) => {
    // Helper per estrarre solo la data da ISO timestamp
    const formatDateForInput = (isoDate: string | null) => {
      if (!isoDate) return null;
      // Estrae solo YYYY-MM-DD da timestamp ISO
      return isoDate.split('T')[0];
    };

    // Controlla se lo status è personalizzato
    const predefinedStatuses = ['in attesa di conferma', 'in attesa di pagamento', 'in lavorazione', 'pronto', 'pronta per la consegna', 'consegnato', 'annullato', 'non convenientemente reparabile'];
    const isCustomStatus = device.current_status && !predefinedStatuses.includes(device.current_status);

    if (isCustomStatus) {
      setCustomStatus(device.current_status);
      setShowCustomStatusInput(true);
    } else {
      setCustomStatus('');
      setShowCustomStatusInput(false);
    }

    setEditingDevice({
      ...device,
      device_type: device.device_type || 'turbina',
      // Formatta le date per i campi input type="date"
      acceptance_date: formatDateForInput(device.acceptance_date),
      approval_date: formatDateForInput(device.approval_date),
      estimated_delivery_date: formatDateForInput(device.estimated_delivery_date)
    });
    setShowNewDeviceForm(false);
  };

  const handleUpdateDevice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDevice) return;

    const success = await updateDevice(editingDevice.id, editingDevice);
    if (success) {
      setEditingDevice(null);
      setCustomStatus('');
      setShowCustomStatusInput(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingDevice(null);
    setCustomStatus('');
    setShowCustomStatusInput(false);
  };

  // Toggle abilita/disabilita utente
  const toggleDeviceActive = async (deviceId: number) => {
    setTogglingDeviceId(deviceId);
    try {
      const response = await fetch(`${apiBaseUrl}/devices/users/${deviceId}/toggle`, {
        method: 'PATCH',
        headers: waAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        await refreshDevices(false); // Ricarica tutti (non solo attivi)
      }
    } catch (err) {
      console.error('Errore toggle device:', err);
    } finally {
      setTogglingDeviceId(null);
    }
  };

  // Elimina device con conferma modale
  const handleDeleteDevice = async () => {
    if (!deviceToDelete) return;
    setDeletingDevice(true);
    try {
      await deleteDevice(deviceToDelete.id);
      setDeviceToDelete(null);
      await refreshDevices(false);
    } catch (err) {
      console.error('Errore eliminazione device:', err);
    } finally {
      setDeletingDevice(false);
    }
  };

  const getStatusColor = (deviceStatus: string) => {
    switch (deviceStatus) {
      case 'online': return 'text-green-400 bg-green-900/30';
      case 'offline': return 'text-gray-400 bg-gray-800/30';
      case 'warning': return 'text-yellow-400 bg-yellow-900/30';
      case 'error': return 'text-red-400 bg-red-900/30';
      case 'maintenance': return 'text-blue-400 bg-blue-900/30';
      default: return 'text-gray-400 bg-gray-800/30';
    }
  };

  const getConnectionStatusColor = (connStatus: string) => {
    switch (connStatus) {
      case 'connected': return 'text-green-400';
      case 'connecting': return 'text-yellow-400';
      case 'qr_ready': return 'text-blue-400';
      default: return 'text-gray-400';
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 p-6">
      {/* Header con Status Badge */}
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">WhatsApp AI Bot</h1>
            <p className="text-sm text-slate-400">Sistema monitoraggio apparati con AI</p>
          </div>

          {/* Status Badge Compatto - cliccabile per aprire modal dettagli */}
          <button
            onClick={() => setShowStatusModal(true)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all hover:scale-105 ${
              isAllOk
                ? 'bg-green-900/30 border-green-600 text-green-400'
                : hasError
                  ? 'bg-red-900/30 border-red-600 text-red-400 animate-pulse'
                  : 'bg-yellow-900/30 border-yellow-600 text-yellow-400'
            }`}
          >
            {isAllOk ? (
              <Wifi className="w-5 h-5" />
            ) : hasError ? (
              <AlertCircle className="w-5 h-5" />
            ) : (
              <WifiOff className="w-5 h-5" />
            )}
            <span className="font-semibold text-sm">
              {isAllOk ? 'Sistema OK' : hasError ? 'Attenzione' : 'Controllo...'}
            </span>
          </button>
        </div>

        {/* Connection Status Card */}
        <div className="bg-slate-800 rounded-lg p-4 mb-4 border border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div>
                <h2 className="text-sm font-medium text-slate-400">Connessione WhatsApp</h2>
                <p className={`text-lg font-semibold ${getConnectionStatusColor(status)}`}>
                  {status === 'connected' && '● Connesso'}
                  {status === 'connecting' && '● Connessione...'}
                  {status === 'qr_ready' && '● QR Pronto'}
                  {status === 'disconnected' && '○ Disconnesso'}
                </p>
              </div>

              <div className="border-l border-slate-700 pl-6 flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Server className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-400">Backend:</span>
                  <span className={`text-sm font-medium ${health?.status === 'ok' ? 'text-green-400' : 'text-red-400'}`}>
                    {health?.status === 'ok' ? 'Online' : 'Offline'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Database className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-400">Database:</span>
                  <span className={`text-sm font-medium ${health?.database === 'connected' ? 'text-green-400' : 'text-red-400'}`}>
                    {health?.database === 'connected' ? 'Connesso' : 'Disconnesso'}
                  </span>
                </div>
                <button
                  onClick={onRefreshHealth}
                  className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                  title="Ricarica stato"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex gap-2">
              {connected ? (
                <button
                  onClick={handleDisconnect}
                  disabled={waLoading}
                  className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-slate-600 text-white rounded text-sm font-medium transition"
                >
                  {waLoading ? 'Disconnessione...' : '● Connesso'}
                </button>
              ) : (
                <button
                  onClick={handleConnect}
                  disabled={waLoading}
                  className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded text-sm font-medium transition"
                >
                  {waLoading ? 'Connessione...' : 'Connetti'}
                </button>
              )}
            </div>
          </div>

          {/* QR Code con banner prominente */}
          {qrCode && (
            <div className="mt-3">
              {/* Banner di avviso */}
              <div className="mb-3 p-3 bg-blue-900/50 border border-blue-500 rounded-lg flex items-center gap-3 animate-pulse">
                <span className="text-2xl">📱</span>
                <div>
                  <p className="text-blue-200 font-semibold">Scansione QR Code richiesta!</p>
                  <p className="text-blue-300 text-sm">Apri WhatsApp sul telefono e scansiona il codice QR per collegare il bot.</p>
                </div>
              </div>
              {/* QR Code */}
              <div className="p-3 bg-white rounded inline-block">
                <img src={qrCode} alt="QR Code WhatsApp" className="w-48 h-48" />
                <p className="text-center text-slate-800 text-xs mt-1">
                  Scansiona con WhatsApp
                </p>
              </div>
            </div>
          )}

          {/* Error */}
          {waError && (
            <div className="mt-3 p-2 bg-red-900/50 border border-red-700 rounded text-red-200 text-xs">
              {waError}
            </div>
          )}
        </div>

        {/* Devices Section - TEST DATABASE */}
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => setExpandedSection(expandedSection === 'devices' ? null : 'devices')}>
              {expandedSection === 'devices' ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronUp className="w-5 h-5 text-slate-400" />}
              <div>
                <h2 className="text-lg font-semibold text-white">Clienti e Apparati</h2>
                <p className="text-xs text-slate-500">Gestione clienti autorizzati e loro apparati</p>
              </div>
            </div>
            {expandedSection === 'devices' && (
              <button
                onClick={() => {
                  setShowNewDeviceForm(!showNewDeviceForm);
                  if (editingDevice) {
                    setEditingDevice(null);
                    setCustomStatus('');
                    setShowCustomStatusInput(false);
                  }
                  if (showNewDeviceForm) {
                    setNewCustomStatus('');
                    setShowNewCustomStatusInput(false);
                  }
                }}
                className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded text-sm font-medium transition"
              >
                {showNewDeviceForm ? 'Annulla' : '+ Nuovo'}
              </button>
            )}
          </div>

          {expandedSection === 'devices' && (
            <>
              {/* New Device Form */}
              {/* Edit Device Form */}
              {editingDevice && (
            <form onSubmit={handleUpdateDevice} className="mb-4 p-3 bg-blue-900/20 rounded border border-blue-700">
              <div className="text-sm font-medium text-blue-400 mb-2">Modifica Apparato</div>
              <div className="grid grid-cols-3 gap-2 mb-2">
                <div>
                  <input
                    type="text"
                    value={editingDevice.customer_name || ''}
                    onChange={(e) => setEditingDevice({ ...editingDevice, customer_name: e.target.value })}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white placeholder-slate-400 text-sm focus:outline-none focus:border-blue-500"
                    placeholder="Nome Cliente *"
                    required
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    value={editingDevice.customer_phone || ''}
                    onChange={(e) => setEditingDevice({ ...editingDevice, customer_phone: e.target.value })}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white placeholder-slate-400 text-sm focus:outline-none focus:border-blue-500"
                    placeholder="Telefono (+39...)"
                    required
                  />
                </div>
                <div>
                  <select
                    value={editingDevice.device_type || 'turbina'}
                    onChange={(e) => setEditingDevice({ ...editingDevice, device_type: e.target.value })}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-blue-500"
                  >
                    <option value="turbina">Turbina</option>
                    <option value="iniettori">Iniettori</option>
                    <option value="fap">FAP</option>
                    <option value="other">Altro</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <input
                    type="text"
                    value={editingDevice.device_code || ''}
                    onChange={(e) => setEditingDevice({ ...editingDevice, device_code: e.target.value })}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white placeholder-slate-400 text-sm focus:outline-none focus:border-blue-500"
                    placeholder="Codice (TRB-01)"
                    required
                  />
                </div>
                <div>
                  <input
                    type="text"
                    value={editingDevice.device_name || ''}
                    onChange={(e) => setEditingDevice({ ...editingDevice, device_name: e.target.value })}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white placeholder-slate-400 text-sm focus:outline-none focus:border-blue-500"
                    placeholder="Nome Apparato"
                    required
                  />
                </div>
                <div>
                  <select
                    value={(() => {
                      const predefined = ['in attesa di conferma', 'in attesa di pagamento', 'in lavorazione', 'pronto', 'pronta per la consegna', 'consegnato', 'annullato', 'non convenientemente reparabile'];
                      return editingDevice.current_status && !predefined.includes(editingDevice.current_status) ? 'altro' : editingDevice.current_status || 'in attesa di conferma';
                    })()}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === 'altro') {
                        const predefined = ['in attesa di conferma', 'in attesa di pagamento', 'in lavorazione', 'pronto', 'pronta per la consegna', 'consegnato', 'annullato', 'non convenientemente reparabile'];
                        const isCustom = editingDevice.current_status && !predefined.includes(editingDevice.current_status);
                        setCustomStatus(isCustom ? editingDevice.current_status : '');
                        setShowCustomStatusInput(true);
                      } else {
                        setEditingDevice({ ...editingDevice, current_status: value });
                        setCustomStatus('');
                        setShowCustomStatusInput(false);
                      }
                    }}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-blue-500"
                  >
                    <option value="in attesa di conferma">In attesa di conferma</option>
                    <option value="in attesa di pagamento">In attesa di pagamento</option>
                    <option value="in lavorazione">In lavorazione</option>
                    <option value="pronto">Pronto</option>
                    <option value="pronta per la consegna">Pronta per la consegna</option>
                    <option value="consegnato">Consegnato</option>
                    <option value="annullato">Annullato</option>
                    <option value="non convenientemente reparabile">Non convenientemente reparabile</option>
                    <option value="altro">Altro</option>
                  </select>
                  {(() => {
                    const predefined = ['in attesa di conferma', 'in attesa di pagamento', 'in lavorazione', 'pronto', 'pronta per la consegna', 'consegnato', 'annullato', 'non convenientemente reparabile'];
                    const isCustomStatus = editingDevice.current_status && !predefined.includes(editingDevice.current_status);
                    return (isCustomStatus || showCustomStatusInput) ? (
                      <input
                        type="text"
                        value={customStatus || (isCustomStatus ? editingDevice.current_status : '')}
                        onChange={(e) => {
                          setCustomStatus(e.target.value);
                          setEditingDevice({ ...editingDevice, current_status: e.target.value });
                        }}
                        className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white placeholder-slate-400 text-sm focus:outline-none focus:border-blue-500 mt-2"
                        placeholder="Inserisci stato personalizzato"
                      />
                    ) : null;
                  })()}
                </div>
                <div>
                  <input
                    type="text"
                    value={editingDevice.location || ''}
                    onChange={(e) => setEditingDevice({ ...editingDevice, location: e.target.value })}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white placeholder-slate-400 text-sm focus:outline-none focus:border-blue-500"
                    placeholder="Posizione"
                  />
                </div>
              </div>

              {/* Campi Economici e Date */}
              <div className="grid grid-cols-6 gap-2 mt-2">
                <div className="text-xs text-slate-400 text-center">💰 Preventivo</div>
                <div className="text-xs text-slate-400 text-center">✅ Pagato</div>
                <div className="col-span-2 text-xs text-slate-400 text-center">🔗 Link Pag. o Pdf prev.</div>
                <div className="text-xs text-slate-400 text-center">📅 Accettazione</div>
                <div className="text-xs text-slate-400 text-center">📋 Approvazione</div>
              </div>
              <div className="grid grid-cols-6 gap-2 mt-1">
                <div>
                  <input
                    type="number"
                    step="0.01"
                    value={editingDevice.estimate_amount || ''}
                    onChange={(e) => setEditingDevice({ ...editingDevice, estimate_amount: e.target.value ? parseFloat(e.target.value) : null })}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white placeholder-slate-400 text-sm focus:outline-none focus:border-blue-500"
                    placeholder="€"
                  />
                </div>
                <div>
                  <label className="flex items-center justify-center gap-2 px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white text-sm cursor-pointer h-full">
                    <input
                      type="checkbox"
                      checked={editingDevice.is_paid || false}
                      onChange={(e) => setEditingDevice({ ...editingDevice, is_paid: e.target.checked })}
                      className="w-4 h-4"
                    />
                  </label>
                </div>
                <div className="col-span-2">
                  <input
                    type="url"
                    value={editingDevice.payment_link || ''}
                    onChange={(e) => setEditingDevice({ ...editingDevice, payment_link: e.target.value })}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white placeholder-slate-400 text-sm focus:outline-none focus:border-blue-500"
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <input
                    type="date"
                    value={editingDevice.acceptance_date || ''}
                    onChange={(e) => setEditingDevice({ ...editingDevice, acceptance_date: e.target.value || null })}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <input
                    type="date"
                    value={editingDevice.approval_date || ''}
                    onChange={(e) => setEditingDevice({ ...editingDevice, approval_date: e.target.value || null })}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-6 gap-2 mt-2">
                <div className="text-xs text-slate-400 text-center">🚚 Consegna</div>
              </div>
              <div className="grid grid-cols-6 gap-2 mt-1">
                <div>
                  <input
                    type="date"
                    value={editingDevice.estimated_delivery_date || ''}
                    onChange={(e) => setEditingDevice({ ...editingDevice, estimated_delivery_date: e.target.value || null })}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="mt-2 flex gap-2">
                <button
                  type="submit"
                  className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium transition"
                >
                  Salva Modifiche
                </button>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-3 py-1 bg-slate-600 hover:bg-slate-700 text-white rounded text-sm transition"
                >
                  Annulla
                </button>
              </div>
            </form>
          )}

          {showNewDeviceForm && (
            <form onSubmit={handleCreateDevice} className="mb-4 p-3 bg-slate-700/50 rounded border border-slate-600">
              <div className="grid grid-cols-3 gap-2 mb-2">
                <div>
                  <input
                    type="text"
                    value={newDevice.customer_name}
                    onChange={(e) => setNewDevice({ ...newDevice, customer_name: e.target.value })}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white placeholder-slate-400 text-sm focus:outline-none focus:border-primary-500"
                    placeholder="Nome Cliente *"
                    required
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    value={newDevice.customer_phone}
                    onChange={(e) => setNewDevice({ ...newDevice, customer_phone: e.target.value })}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white placeholder-slate-400 text-sm focus:outline-none focus:border-primary-500"
                    placeholder="Telefono (+39...)"
                    required
                  />
                </div>
                <div>
                  <select
                    value={newDevice.device_type}
                    onChange={(e) => setNewDevice({ ...newDevice, device_type: e.target.value as any })}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-primary-500"
                  >
                    <option value="turbina">Turbina</option>
                    <option value="iniettori">Iniettori</option>
                    <option value="fap">FAP</option>
                    <option value="other">Altro</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <input
                    type="text"
                    value={newDevice.device_code}
                    onChange={(e) => setNewDevice({ ...newDevice, device_code: e.target.value })}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white placeholder-slate-400 text-sm focus:outline-none focus:border-primary-500"
                    placeholder="Codice (TRB-01)"
                    required
                  />
                </div>
                <div>
                  <input
                    type="text"
                    value={newDevice.device_name}
                    onChange={(e) => setNewDevice({ ...newDevice, device_name: e.target.value })}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white placeholder-slate-400 text-sm focus:outline-none focus:border-primary-500"
                    placeholder="Nome Apparato"
                    required
                  />
                </div>
                <div>
                  <select
                    value={(() => {
                      const predefined = ['in attesa di conferma', 'in attesa di pagamento', 'in lavorazione', 'pronto', 'pronta per la consegna', 'consegnato', 'annullato', 'non convenientemente reparabile'];
                      return newDevice.current_status && !predefined.includes(newDevice.current_status) ? 'altro' : newDevice.current_status || 'in attesa di conferma';
                    })()}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === 'altro') {
                        const predefined = ['in attesa di conferma', 'in attesa di pagamento', 'in lavorazione', 'pronto', 'pronta per la consegna', 'consegnato', 'annullato', 'non convenientemente reparabile'];
                        const isCustom = newDevice.current_status && !predefined.includes(newDevice.current_status);
                        setNewCustomStatus(isCustom ? newDevice.current_status : '');
                        setShowNewCustomStatusInput(true);
                      } else {
                        setNewDevice({ ...newDevice, current_status: value });
                        setNewCustomStatus('');
                        setShowNewCustomStatusInput(false);
                      }
                    }}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-primary-500"
                  >
                    <option value="in attesa di conferma">In attesa di conferma</option>
                    <option value="in attesa di pagamento">In attesa di pagamento</option>
                    <option value="in lavorazione">In lavorazione</option>
                    <option value="pronto">Pronto</option>
                    <option value="pronta per la consegna">Pronta per la consegna</option>
                    <option value="consegnato">Consegnato</option>
                    <option value="annullato">Annullato</option>
                    <option value="non convenientemente reparabile">Non convenientemente reparabile</option>
                    <option value="altro">Altro</option>
                  </select>
                  {(() => {
                    const predefined = ['in attesa di conferma', 'in attesa di pagamento', 'in lavorazione', 'pronto', 'pronta per la consegna', 'consegnato', 'annullato', 'non convenientemente reparabile'];
                    const isCustomStatus = newDevice.current_status && !predefined.includes(newDevice.current_status);
                    return (isCustomStatus || showNewCustomStatusInput) ? (
                      <input
                        type="text"
                        value={newCustomStatus || (isCustomStatus ? newDevice.current_status : '')}
                        onChange={(e) => {
                          setNewCustomStatus(e.target.value);
                          setNewDevice({ ...newDevice, current_status: e.target.value });
                        }}
                        className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white placeholder-slate-400 text-sm focus:outline-none focus:border-primary-500 mt-2"
                        placeholder="Inserisci stato personalizzato"
                      />
                    ) : null;
                  })()}
                </div>
                <div>
                  <input
                    type="text"
                    value={newDevice.location}
                    onChange={(e) => setNewDevice({ ...newDevice, location: e.target.value })}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white placeholder-slate-400 text-sm focus:outline-none focus:border-primary-500"
                    placeholder="Posizione"
                  />
                </div>
              </div>

              {/* Campi Economici e Date */}
              <div className="grid grid-cols-6 gap-2 mt-2">
                <div className="text-xs text-slate-400 text-center">💰 Preventivo</div>
                <div className="text-xs text-slate-400 text-center">✅ Pagato</div>
                <div className="col-span-2 text-xs text-slate-400 text-center">🔗 Link Pag. o Pdf prev.</div>
                <div className="text-xs text-slate-400 text-center">📅 Accettazione</div>
                <div className="text-xs text-slate-400 text-center">📋 Approvazione</div>
              </div>
              <div className="grid grid-cols-6 gap-2 mt-1">
                <div>
                  <input
                    type="number"
                    step="0.01"
                    value={newDevice.estimate_amount || ''}
                    onChange={(e) => setNewDevice({ ...newDevice, estimate_amount: e.target.value ? parseFloat(e.target.value) : null })}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white placeholder-slate-400 text-sm focus:outline-none focus:border-primary-500"
                    placeholder="€"
                  />
                </div>
                <div>
                  <label className="flex items-center justify-center gap-2 px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white text-sm cursor-pointer h-full">
                    <input
                      type="checkbox"
                      checked={newDevice.is_paid}
                      onChange={(e) => setNewDevice({ ...newDevice, is_paid: e.target.checked })}
                      className="w-4 h-4"
                    />
                  </label>
                </div>
                <div className="col-span-2">
                  <input
                    type="url"
                    value={newDevice.payment_link || ''}
                    onChange={(e) => setNewDevice({ ...newDevice, payment_link: e.target.value })}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white placeholder-slate-400 text-sm focus:outline-none focus:border-primary-500"
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <input
                    type="date"
                    value={newDevice.acceptance_date || ''}
                    onChange={(e) => setNewDevice({ ...newDevice, acceptance_date: e.target.value || null })}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-primary-500"
                  />
                </div>
                <div>
                  <input
                    type="date"
                    value={newDevice.approval_date || ''}
                    onChange={(e) => setNewDevice({ ...newDevice, approval_date: e.target.value || null })}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-primary-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-6 gap-2 mt-2">
                <div className="text-xs text-slate-400 text-center">🚚 Consegna</div>
              </div>
              <div className="grid grid-cols-6 gap-2 mt-1">
                <div>
                  <input
                    type="date"
                    value={newDevice.estimated_delivery_date || ''}
                    onChange={(e) => setNewDevice({ ...newDevice, estimated_delivery_date: e.target.value || null })}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-primary-500"
                  />
                </div>
              </div>

              <div className="mt-2 flex gap-2">
                <button
                  type="submit"
                  className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-medium transition"
                >
                  Crea Cliente
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowNewDeviceForm(false);
                    setNewCustomStatus('');
                    setShowNewCustomStatusInput(false);
                  }}
                  className="px-3 py-1 bg-slate-600 hover:bg-slate-700 text-white rounded text-sm transition"
                >
                  Annulla
                </button>
              </div>
            </form>
          )}

          {/* Devices List */}
          {devicesLoading && (
            <div className="text-center py-4 text-slate-400 text-sm">
              Caricamento...
            </div>
          )}

          {devicesError && (
            <div className="p-2 bg-red-900/50 border border-red-700 rounded text-red-200 text-xs">
              {devicesError}
            </div>
          )}

          {!devicesLoading && !devicesError && devices.length === 0 && (
            <div className="text-center py-4 text-slate-400 text-sm">
              Nessun apparato test. Creane uno per testare le query AI.
            </div>
          )}

          {!devicesLoading && devices.length > 0 && (
            <div className="space-y-2">
              {devices.map((device) => (
                <div
                  key={device.id}
                  className={`p-3 rounded border transition ${
                    device.is_active
                      ? 'bg-green-900/20 border-green-700/50 hover:border-green-500'
                      : 'bg-red-900/20 border-red-700/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(device.current_status)}`}>
                        {device.current_status}
                      </span>
                      <span className="text-sm font-bold text-primary-400">{(device as any).customer_name || 'N/A'}</span>
                      <span className="text-xs text-slate-500">{(device as any).customer_phone || ''}</span>
                      <span className="text-xs font-mono text-slate-400">{device.device_code}</span>
                      <span className="text-sm font-medium text-white">{device.device_name}</span>
                      {device.device_type && <span className="text-xs text-slate-500 capitalize">({device.device_type})</span>}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => toggleDeviceActive(device.id)}
                        disabled={togglingDeviceId === device.id}
                        className={`p-1.5 rounded text-sm transition ${
                          device.is_active
                            ? 'bg-green-600/80 hover:bg-green-600'
                            : 'bg-red-600/80 hover:bg-red-600'
                        } text-white disabled:opacity-50`}
                        title={device.is_active ? 'Disabilita apparato' : 'Abilita apparato'}
                      >
                        {togglingDeviceId === device.id ? '⏳' : device.is_active ? '⏸️' : '▶️'}
                      </button>
                      <button
                        onClick={() => handleEditDevice(device)}
                        className="p-1.5 bg-slate-600/80 hover:bg-slate-600 text-white rounded text-sm transition"
                        title="Modifica apparato"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => setDeviceToDelete({
                          id: device.id,
                          name: (device as any).customer_name || device.device_name || device.device_code
                        })}
                        className="p-1.5 bg-red-700/80 hover:bg-red-700 text-white rounded text-sm transition"
                        title="Elimina apparato"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
            </>
          )}
        </div>

        {/* Conversations Viewer */}
        <div className="mt-4 bg-slate-800 rounded-lg border border-slate-700 p-3">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => setExpandedSection(expandedSection === 'conversations' ? null : 'conversations')}
          >
            {expandedSection === 'conversations' ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronUp className="w-5 h-5 text-slate-400" />}
            <h3 className="text-sm font-medium text-slate-300">
              {expandedSection === 'conversations' ? 'Nascondi Log Conversazioni' : 'Mostra Log Conversazioni'}
            </h3>
          </div>
        </div>
        {expandedSection === 'conversations' && (
          <div className="mt-2">
            <ConversationsViewer />
          </div>
        )}

        {/* Configuration Panel */}
        <div className="mt-4 bg-slate-800 rounded-lg border border-slate-700 p-3">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => setExpandedSection(expandedSection === 'config' ? null : 'config')}
          >
            {expandedSection === 'config' ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronUp className="w-5 h-5 text-slate-400" />}
            <h3 className="text-sm font-medium text-slate-300">
              {expandedSection === 'config' ? 'Nascondi Configurazione Bot' : 'Mostra Configurazione Bot'}
            </h3>
          </div>
        </div>
        {expandedSection === 'config' && (
          <div className="mt-2">
            <ConfigPanel />
          </div>
        )}

        {/* Test Simulator Panel */}
        <div className="mt-4 bg-slate-800 rounded-lg border border-purple-700/50 p-3">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => setExpandedSection(expandedSection === 'test' ? null : 'test')}
          >
            {expandedSection === 'test' ? <ChevronDown className="w-5 h-5 text-purple-400" /> : <ChevronUp className="w-5 h-5 text-purple-400" />}
            <FlaskConical className="w-5 h-5 text-purple-400" />
            <h3 className="text-sm font-medium text-purple-300">
              {expandedSection === 'test' ? 'Nascondi Simulatore Test' : 'Mostra Simulatore Test'}
            </h3>
            <span className="px-2 py-0.5 bg-purple-900/50 text-purple-300 text-xs rounded">🧪 Dev Tool</span>
          </div>
        </div>
        {expandedSection === 'test' && (
          <div className="mt-2">
            <TestSimulatorPanel />
          </div>
        )}
      </div>

      {/* Modal Conferma Eliminazione Device */}
      {deviceToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-white mb-3">🗑️ Elimina Apparato</h3>
            <p className="text-slate-300 mb-4">
              Eliminare l'apparato <strong className="text-white">{deviceToDelete.name}</strong>?
            </p>
            <p className="text-sm text-slate-400 mb-6">
              <strong className="text-red-400">Questa operazione non può essere annullata!</strong>
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeviceToDelete(null)}
                disabled={deletingDevice}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded font-medium transition disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                onClick={handleDeleteDevice}
                disabled={deletingDevice}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium transition disabled:opacity-50"
              >
                {deletingDevice ? 'Eliminazione...' : 'Elimina'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Status Dettagliato */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">📊 Stato Sistema</h3>
              <button
                onClick={() => setShowStatusModal(false)}
                className="p-1 hover:bg-slate-700 rounded transition"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            {/* Status Items */}
            <div className="space-y-3 mb-6">
              {/* WhatsApp Connection */}
              <div className={`flex items-center justify-between p-3 rounded-lg border ${
                connected ? 'bg-green-900/20 border-green-700' : 'bg-red-900/20 border-red-700'
              }`}>
                <div className="flex items-center gap-2">
                  <Wifi className={`w-5 h-5 ${connected ? 'text-green-400' : 'text-red-400'}`} />
                  <span className="text-slate-200">WhatsApp</span>
                </div>
                <span className={`font-semibold ${connected ? 'text-green-400' : 'text-red-400'}`}>
                  {connected ? '● Connesso' : '○ Disconnesso'}
                </span>
              </div>

              {/* Bot Active */}
              <div className={`flex items-center justify-between p-3 rounded-lg border ${
                botActive ? 'bg-green-900/20 border-green-700' : 'bg-red-900/20 border-red-700'
              }`}>
                <div className="flex items-center gap-2">
                  <AlertCircle className={`w-5 h-5 ${botActive ? 'text-green-400' : 'text-red-400'}`} />
                  <span className="text-slate-200">Bot Attivo</span>
                </div>
                <span className={`font-semibold ${botActive ? 'text-green-400' : 'text-red-400'}`}>
                  {botActive ? '● Attivo' : '○ Disattivato'}
                </span>
              </div>

              {/* Backend */}
              <div className={`flex items-center justify-between p-3 rounded-lg border ${
                health?.status === 'ok' ? 'bg-green-900/20 border-green-700' : 'bg-red-900/20 border-red-700'
              }`}>
                <div className="flex items-center gap-2">
                  <Server className={`w-5 h-5 ${health?.status === 'ok' ? 'text-green-400' : 'text-red-400'}`} />
                  <span className="text-slate-200">Backend</span>
                </div>
                <span className={`font-semibold ${health?.status === 'ok' ? 'text-green-400' : 'text-red-400'}`}>
                  {health?.status === 'ok' ? '● Online' : '○ Offline'}
                </span>
              </div>

              {/* Database */}
              <div className={`flex items-center justify-between p-3 rounded-lg border ${
                health?.database === 'connected' ? 'bg-green-900/20 border-green-700' : 'bg-red-900/20 border-red-700'
              }`}>
                <div className="flex items-center gap-2">
                  <Database className={`w-5 h-5 ${health?.database === 'connected' ? 'text-green-400' : 'text-red-400'}`} />
                  <span className="text-slate-200">Database</span>
                </div>
                <span className={`font-semibold ${health?.database === 'connected' ? 'text-green-400' : 'text-red-400'}`}>
                  {health?.database === 'connected' ? '● Connesso' : '○ Disconnesso'}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  onRefreshHealth();
                  setShowStatusModal(false);
                }}
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded font-medium transition flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Aggiorna
              </button>
              {!connected ? (
                <button
                  onClick={() => {
                    connect();
                    setShowStatusModal(false);
                  }}
                  disabled={waLoading}
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-slate-600 text-white rounded font-medium transition"
                >
                  {waLoading ? 'Connessione...' : 'Connetti WhatsApp'}
                </button>
              ) : (
                <button
                  onClick={() => {
                    disconnect();
                    setShowStatusModal(false);
                  }}
                  disabled={waLoading}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-slate-600 text-white rounded font-medium transition"
                >
                  Disconnetti
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
